/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, inject} from '@angular/core';
import * as _ from 'lodash';
import template from './report-schedule-edit-page.component.html';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {ADN_REPORT_TYPES} from '../report/report-types';
import {EmailHelper} from '../../components/util/email';
import {
  afterSaveAfterInitMods,
  beforeSubmitMods,
  filterReportTemplates
} from './report-schedule-commons';
import {ReportScheduleModalService} from './report-schedule-modal.service';
import {ReportTemplateApiService} from '../report-template/report-template-api.service';
import {UserApiService} from '../admin/user/user-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SearchApiService} from '../common/controller/search-api.service';
import {ReportScheduleApiService, ReportScheduleModel} from './report-schedule-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {TimepickerModule} from 'ngx-bootstrap/timepicker';
import {AppSharedModule} from '../common/app-shared.module';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {NextReportComponent} from './next-report.component';
import {ReportUrlComponent} from './report-url-component';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, UIRouterModule, ButtonsModule, TimepickerModule, AdnSelectComponent, NextReportComponent, ReportUrlComponent],
  selector: 'adn-report-schedule-edit-page',
  template
})
export class ReportScheduleEditPageComponent extends SignalAdminResourceEditPageBase<ReportScheduleModel> {
  readonly weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  availableCurrencies: string[] = [];
  reportTemplates: any[] = [];
  allAdvertisers = false;
  recipientEmailsSaved: {success?: boolean} | null = null;

  lineItemResults: any[] = [];
  adUnitResults: any[] = [];
  orderResults: any[] = [];
  siteResults: any[] = [];
  siteGroupResults: any[] = [];
  salespersonResults: any[] = [];
  earningsAccountResults: any[] = [];
  advertiserResults: any[] = [];

  private readonly scheduleLocale: string;
  private readonly preferredCurrency: string;
  private readonly timeZone: string;
  private initialized = false;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly userApi: UserApiService,
    private readonly searchApi: SearchApiService,
    private readonly reportTemplateApi: ReportTemplateApiService,
    private readonly reportScheduleModalService: ReportScheduleModalService,
    private readonly ngZone: NgZone
  ) {
    super(inject(ReportScheduleApiService), 'reportSchedule');
    this.scheduleLocale = localUserProfile.get('locale') || 'en_GB';
    this.preferredCurrency = localUserProfile.getPreferredCurrency();
    this.timeZone = LocalNetworkProfile.get('timeZone') || 'UTC';
  }

  get lineItemReportTemplates(): any[] {
    return _.filter(this.reportTemplates, {reportType: 'LINE_ITEM'});
  }

  get reportType(): string | undefined {
    return this.editingModel?.reportTemplate?.reportType;
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  editRecipients(): void {
    this.reportScheduleModalService.openRecipientsModal({
      recipientUsers: this.editingModel.recipientUsers,
      isNew: this.isNew,
      scheduleModel: this.isNew ? null : this.model
    }, (result) => {
      if (!result) {
        return;
      }
      if (result.recipientUsers) {
        this.editingModel.recipientUsers = result.recipientUsers;
      }
      if (result.validationWarnings) {
        this.editingModel.validationWarnings = result.validationWarnings;
      }
      if (result.success !== undefined) {
        this.recipientEmailsSaved = {success: result.success};
        if (result.success) {
          // Saved straight to the server: keep page state in step so Undo / later saves don't revert it.
          this.state.update({
            ...this.model,
            recipientUsers: result.recipientUsers || this.model.recipientUsers,
            validationWarnings: result.validationWarnings || this.model.validationWarnings
          });
        }
      } else if (result.recipientUsers) {
        this.markExternalFormDirty();
      }
    });
  }

  searchLineItems(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'LineItem'}).subscribe((data) => {
      this.lineItemResults = data.searchResults;
    });
  }

  searchAdUnits(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'AdUnit'}).subscribe((data) => {
      this.adUnitResults = data.searchResults;
    });
  }

  searchOrders(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'Order'}).subscribe((data) => {
      this.orderResults = data.searchResults;
    });
  }

  searchSites(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'Site'}).subscribe((data) => {
      this.siteResults = data.searchResults;
    });
  }

  searchSiteGroups(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'SiteGroup'}).subscribe((data) => {
      this.siteGroupResults = data.searchResults;
    });
  }

  searchSalesperson(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'User'}).subscribe((data) => {
      this.salespersonResults = data.searchResults;
    });
  }

  searchEarningsAccounts(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'EarningsAccount'}).subscribe((data) => {
      this.earningsAccountResults = data.searchResults;
    });
  }

  searchAdvertisers(searchElement: AdnSelectSearchElement): void {
    this.searchApi.search({q: searchElement.search, types: 'Advertiser'}).subscribe((data) => {
      this.advertiserResults = data.searchResults;
    });
  }

  protected override afterEditingModelReset(): void {
    this.availableCurrencies = this.LocalNetworkProfile.get('availableCurrencies');
    this.combineEmails();

    // Runs on every reset (load, save, Undo): Undo rebuilds editingModel from state.model().
    if (this.isNew) {
      this.editingModel.period = ADN_REPORT_TYPES.LINE_ITEM.defaultPeriod.id;
      this.editingModel.recurrence = 'WEEKLY';
      this.editingModel.timeZone = this.LocalNetworkProfile.get('timeZone') || 'UTC';
      this.editingModel.locale = this.scheduleLocale;
      this.allAdvertisers = false;
    }

    this.allAdvertisers = !!_.get(this.editingModel.parameters || {}, 'ALL_ACTIVE', false);
    this.editingModel.currency = this.editingModel.currency || this.preferredCurrency || this.LocalNetworkProfile.get('defaultCurrency');
    this.selectLoadedReportTemplate();

    if (this.initialized) {
      return;
    }
    this.initialized = true;

    if (this.model.recipientUsers?.length > 0) {
      this.userApi.listOptions({ids: _.map(this.model.recipientUsers, 'id')}).subscribe((page) => {
        const allUsers = page.results;
        _.forEach(this.editingModel.recipientUsers, (ru: any) => {
          const match = _.find(allUsers, ['id', ru.id]);
          if (match) {
            ru.displayName = match.displayName;
          }
        });
      });
    }

    const perms = this.LocalUserPermissions.getAllNetworkPermissions();
    const anyPerms = this.LocalUserPermissions.getAllVisPermissions();
    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    const canSeeAdvertiserReports = networkDefaults.runReportsOptions.indexOf('ADVERTISER_REPORTS') > -1;

    this.reportTemplateApi.listOptions().subscribe((page) => {
      this.ngZone.run(() => {
        this.reportTemplates = filterReportTemplates(page.results, perms, anyPerms, canSeeAdvertiserReports);
        this.selectLoadedReportTemplate();
      });
    });
  }

  /** The template select binds by reference, so swap in the loaded instance after each reset. */
  private selectLoadedReportTemplate(): void {
    const selRepTemplate = _.find(this.reportTemplates, ['id', _.get(this.editingModel, ['reportTemplate', 'id'])]);
    if (selRepTemplate) {
      this.editingModel.reportTemplate = selRepTemplate;
    }
  }

  protected override prepareForSubmit(model: ReportScheduleModel): void {
    model.parameters = model.parameters || {};

    _.forEach(ADN_REPORT_TYPES, (rt) => {
      delete model.parameters[rt.idKey];
    });
    delete model.parameters.relatedLineItem;
    delete model.parameters[ADN_REPORT_TYPES.ADVERTISER.allActiveFlag];

    const theReportType = ADN_REPORT_TYPES[model.reportTemplate.reportType];
    if (theReportType === ADN_REPORT_TYPES.ADVERTISER && this.allAdvertisers) {
      model.parameters[ADN_REPORT_TYPES.ADVERTISER.allActiveFlag] = true;
    } else {
      if (theReportType === ADN_REPORT_TYPES.LINE_ITEM) {
        model.parameters[theReportType.idKey] = model.lineItem.id;
        model.parameters.relatedLineItem = [{id: model.lineItem.id}];
      } else if (theReportType === ADN_REPORT_TYPES.NETWORK) {
        model.parameters[theReportType.idKey] = this.LocalNetworkProfile.getNetworkId();
      } else {
        model.parameters[theReportType.idKey] = model[theReportType.objKey].id;
      }
    }

    beforeSubmitMods(model, this.timeZone);

    model.recipientEmailAddresses = _.filter(_.map((model.recipientEmailAddressesCombined || '').split(','), (email) => {
      return _.trim(email);
    }), (email) => {
      return email.length > 0 && EmailHelper.isEmail(email);
    });
  }

  private combineEmails(): void {
    this.editingModel.recipientEmailAddressesCombined = (this.editingModel.recipientEmailAddresses || []).join(', ');
    afterSaveAfterInitMods(this.editingModel, this.adnTimeShifter, this.timeZone, this.scheduleLocale);
  }
}
